/**
 * The AI panel's open/closed state, shared between the command bar's Ask
 * button, the rail, and any screen that wants to hand the model a question
 * ("Explain this statement cycle").
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

export interface AiPanelContextValue {
  open: boolean
  setOpen: (open: boolean) => void
  toggle: () => void
  /** Column width in pixels on wide screens. Ignored while expanded. */
  width: number
  setWidth: (width: number) => void
  /** Full-viewport reading mode — for a long answer with charts in it. */
  expanded: boolean
  setExpanded: (expanded: boolean) => void
  /**
   * A question a screen has queued for the panel. `AiPanel` should consume it,
   * send it, and call `clearPrompt()`.
   */
  prompt: string | null
  /** Opens the panel with a question already in hand. */
  ask: (prompt: string) => void
  clearPrompt: () => void
}

const AiPanelContext = createContext<AiPanelContextValue | null>(null)

export function AiPanelProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [prompt, setPrompt] = useState<string | null>(null)
  const [width, setWidthState] = useState(readWidth)
  const [expanded, setExpandedState] = useState(readExpanded)

  const setWidth = useCallback((next: number) => {
    const clamped = clampWidth(next)
    setWidthState(clamped)
    writeStored(WIDTH_KEY, String(clamped))
  }, [])

  const setExpanded = useCallback((next: boolean) => {
    setExpandedState(next)
    writeStored(EXPANDED_KEY, next ? 'on' : 'off')
  }, [])

  const value = useMemo<AiPanelContextValue>(
    () => ({
      open,
      setOpen,
      toggle: () => setOpen((current) => !current),
      width,
      setWidth,
      expanded,
      setExpanded,
      prompt,
      ask: (next: string) => {
        setPrompt(next)
        setOpen(true)
      },
      clearPrompt: () => setPrompt(null),
    }),
    [open, prompt, width, setWidth, expanded, setExpanded],
  )

  return <AiPanelContext.Provider value={value}>{children}</AiPanelContext.Provider>
}

export function useAiPanel(): AiPanelContextValue {
  const context = useContext(AiPanelContext)
  if (!context) throw new Error('useAiPanel must be used inside <AiPanelProvider>.')
  return context
}

/** Safe outside the shell (the login screen renders no panel). */
export function useOptionalAiPanel(): AiPanelContextValue | null {
  return useContext(AiPanelContext)
}

/** The width a panel opens at before anyone has dragged it. */
export const AI_PANEL_WIDTH = 380

/**
 * Bounds on the drag.
 *
 * The floor is where the transcript stops being readable — charts and tables
 * inside an answer need room, and below this they wrap into a column of
 * fragments. The ceiling leaves the screen behind the panel usable, which is
 * the whole reason the panel is a column and not a page; wanting more than this
 * is what the expand button is for.
 */
export const AI_PANEL_MIN_WIDTH = 320
export const AI_PANEL_MAX_WIDTH = 880

const WIDTH_KEY = 'pf.ai.panelWidth'
const EXPANDED_KEY = 'pf.ai.panelExpanded'

export function clampWidth(width: number): number {
  if (!Number.isFinite(width)) return AI_PANEL_WIDTH
  return Math.min(AI_PANEL_MAX_WIDTH, Math.max(AI_PANEL_MIN_WIDTH, Math.round(width)))
}

function writeStored(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // A panel that will not resize is a nuisance; one that throws is a bug.
  }
}

function readWidth(): number {
  try {
    const stored = window.localStorage.getItem(WIDTH_KEY)
    return stored ? clampWidth(Number(stored)) : AI_PANEL_WIDTH
  } catch {
    return AI_PANEL_WIDTH
  }
}

function readExpanded(): boolean {
  try {
    return window.localStorage.getItem(EXPANDED_KEY) === 'on'
  } catch {
    return false
  }
}

export function useAskAi(): (prompt: string) => void {
  const panel = useOptionalAiPanel()
  return useCallback((prompt: string) => panel?.ask(prompt), [panel])
}
